"use client";

import React from "react";
import { Leaf, Lock, Layout, Sun, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const certifications = ["300 Guest Capacity", "Natural Stone Altar", "Integrated Sound System"];
const technicalFeatures = [{
  icon: Leaf,
  title: "Botanical Curation",
  desc: "Manicured landscapes designed for year-round floral elegance."
}, {
  icon: Lock,
  title: "Private Sanctuary",
  desc: "Secluded garden walls ensuring an intimate, exclusive atmosphere."
}, {
  icon: Layout,
  title: "Custom Layouts",
  desc: "Versatile seating arrangements tailored to your ceremony vision."
}, {
  icon: Sun,
  title: "Atmospheric Lighting",
  desc: "Subtle evening illumination for a magical twilight transition."
}];
export default function TheCeremonyGarden() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224639" id="the-ceremony-garden" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_40%,transparent_100%)] opacity-5 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={textRef} className={`lg:col-span-5 space-y-10 transition-all duration-1000 ease-out ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 text-secondary font-mono text-sm tracking-[0.15em] uppercase">
                <span className="w-8 h-px bg-secondary"></span>
                The Estate Grounds
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-medium tracking-tight text-primary leading-tight">
                The Ceremony <br className="hidden lg:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                  Garden
                </span>
              </h2>

              <div className="flex flex-wrap gap-2">
                {certifications.map((cert, i) => <Badge key={i} data-index={i} variant="outline" className="bg-accent/30 text-accent-foreground border-secondary/20 backdrop-blur-sm font-mono text-xs py-1 px-3 rounded-md">
                    {cert}
                  </Badge>)}
              </div>
            </div>

            <p className="text-lg text-muted-foreground text-justify leading-relaxed font-serif">
              Nestled within the heart of VOW, our Ceremony Garden offers an ethereal backdrop for your union. Surrounded by architectural greenery and the soft scent of seasonal blooms, this open-air sanctuary combines natural beauty with modern sophistication. Every detail, from the hand-carved stone altar to the hidden acoustics, is curated to ensure your vows are heard in perfect serenity.
            </p>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8 pt-4 border-t border-border/30">
              {technicalFeatures.map((feature, i) => <div key={i} data-index={i} className="flex flex-col gap-3 group">
                  <div className="size-12 rounded-md bg-card border border-border flex items-center justify-center text-secondary shadow-sm group-hover:border-secondary/50 group-hover:bg-secondary/5 transition-colors duration-300">
                    <feature.icon className="size-6" strokeWidth={1} />
                  </div>
                  <div>
                    <h3 className="font-default font-semibold text-primary text-base mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-snug font-sans">
                      {feature.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-6">
              <Button asChild size="lg" className="group bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-all">
                <Link to="/BeginYourJourneyPage">
                  Begin Your Journey
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`lg:col-span-7 relative transition-all duration-1000 delay-200 ease-out ${imageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="relative aspect-[4/3] md:aspect-[16/10] rounded-md overflow-hidden border border-border/50 shadow-xl shadow-shadow-color/10 group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683483/img/vow_ceremony_garden_wide.png" alt="Wide architectural shot of the VOW ceremony garden with stone altar" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
              {}
              <div className="absolute inset-0 bg-gradient-to-tr from-background/40 via-transparent to-transparent mix-blend-multiply" />
              
              {}
              <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                <div className="bg-background/90 backdrop-blur-md border border-border/50 p-4 rounded-md shadow-md">
                  <div className="text-xs font-mono text-secondary mb-1 tracking-widest">ESTATE VIEW</div>
                  <div className="font-default font-medium text-primary">The Altar at Golden Hour</div>
                </div>
              </div>
            </div>

            {}
            <div className="absolute -bottom-12 -left-12 w-3/5 aspect-square rounded-md overflow-hidden border-4 border-background shadow-2xl shadow-shadow-color/20 hidden md:block z-20">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683483/img/vow_garden_floral_detail.png" alt="Close up of curated floral textures in the ceremony garden" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-secondary/5 mix-blend-overlay" />
            </div>
            
            {}
            <div className="absolute -top-6 -right-6 size-24 bg-[radial-gradient(circle,var(--color-secondary)_1px,transparent_1px)] bg-[size:8px_8px] opacity-30 z-0" />
          </div>

        </div>
      </div>
    </section>;
}